import React, { Component, createRef } from 'react';
import { Grid } from '@material-ui/core';
import {
    AbstractSofaForm,
    NOT_ALLOW_DOUBLE_SPACES_REGEX,
    POSTCODE_IN_SEARCH_MODE_INVALID_ERROR_MESSAGE,
    SofaButton,
    SofaFormControl,
    SofaFormControlDescriptionWithGtm,
    SofaFormFilter,
    SofaFormValidator,
    SofaFormWithGtm,
    SofaInput,
    SofaLink,
    getPropsForSofaInput,
    stringRequiredValidator,
    textFilter
} from '@sofology/sofologycomponents';

import './AddressSearch.scss';

import { AddressModel, AddressSearchEditorModel, PostcodeModel } from 'models';
import { FindAddressFunc, InitAddressSearchEditorFunc } from 'store/checkout/actions/addressSearchEditor';
import { POSTCODE_IN_SERACH_MODE_REQUIRED_ERROR_MESSAGE, SEARCH_HAS_NOT_STARTED_WARNING_MESSAGE } from './addressSearchAdditionalData';
import { analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { Loader } from 'components/Loader/Loader';
import AddressSelector, { AddressSelectorType } from './AddressSelector/AddressSelector';

const COUNT_OF_CONTROLS_IN_SEARCH_MODE = 1;
const COUNT_OF_CONTROLS_IN_SELECT_MODE = 1;
const GTM_FORM_NAME = 'postcodeLookup';

export enum SearchModes {
    NotInitialized = 0,
    Search,
    Select
}

interface OwnProps {
    ref?: React.RefObject<AddressSearchType>;
    editorId: string;
    gtmFormName: string;
    additionalValidators?: Partial<Record<keyof SearchModeFormControls, SofaFormValidator<any>[] | undefined>>;
    additionalFilters?: Partial<Record<keyof SearchModeFormControls, SofaFormFilter<string>[] | undefined>>;
    onChange: (address: AddressModel) => void;
    startControlIndex: number;
    fixedPostcode: string | null;
    addressSearchEditorData: AddressSearchEditorModel | null;
    initAddressSearchEditor: InitAddressSearchEditorFunc;
    findAddress: FindAddressFunc;
}

export interface SearchModeFormControls extends AbstractSofaForm {
    postcode: SofaFormControl<string>;
}

interface OwnState {
    form: SofaFormWithGtm<SearchModeFormControls>;
    currentMode: SearchModes;
    isPostcodeSearchFailed: boolean;
    shouldShowSearchNotStartedErrorMessage: boolean;
}

class AddressSearch extends Component<OwnProps, OwnState> {
    private addressSearchElement = React.createRef<HTMLDivElement>();
    private addressSelector = React.createRef<AddressSelectorType>();

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            currentMode: SearchModes.NotInitialized,
            isPostcodeSearchFailed: false,
            form: new SofaFormWithGtm<SearchModeFormControls>(
                {
                    postcode: {
                        name: 'postcode',
                        defaultValue: '',
                        filters: [
                            ...(this.props.additionalFilters?.postcode ?? []),
                            textFilter(NOT_ALLOW_DOUBLE_SPACES_REGEX, ' ')
                        ],
                        validators: [
                            stringRequiredValidator(POSTCODE_IN_SERACH_MODE_REQUIRED_ERROR_MESSAGE),
                            ...(this.props.additionalValidators?.postcode ?? []),
                            this.postcodeServerValidator(POSTCODE_IN_SEARCH_MODE_INVALID_ERROR_MESSAGE)
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: GTM_FORM_NAME
                    } as SofaFormControlDescriptionWithGtm<string>,
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'form',
                () => this.state,
                this.props.gtmFormName,
                analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
                errorToBasketErrorModel,
                this.props.startControlIndex
            ),
            shouldShowSearchNotStartedErrorMessage: false
        };
    }

    componentDidMount(): void {
        const { editorId } = this.props;
        this.props.initAddressSearchEditor(editorId);

        if (this.props.fixedPostcode) {
            this.props.findAddress(this.props.editorId, new PostcodeModel(this.props.fixedPostcode));
            this.state.form.controls.postcode.setValue(this.props.fixedPostcode);
        } else {
            this.setState({ currentMode: SearchModes.Search });
        }

        if (this.isPostcodeSearchSuccessfullyCompleted()) {
            this.setState({ currentMode: SearchModes.Select });
        }
    }

    componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>): void {
        const isAddressSearchCompleted = prevProps.addressSearchEditorData && this.props.addressSearchEditorData
            && prevProps.addressSearchEditorData.isFindAddressProcessing && !this.props.addressSearchEditorData.isFindAddressProcessing;

        if (isAddressSearchCompleted) {
            this.handleAddressSearchResponse();
        }

        if (this.state.isPostcodeSearchFailed && prevState.form.controls.postcode.value !== this.state.form.controls.postcode.value) {
            this.setState({ isPostcodeSearchFailed: false }, () => {
                this.state.form.controls.postcode.validate();
            });
        }
    }

    handleFindAddressClick = async () => {
        this.setState({ shouldShowSearchNotStartedErrorMessage: false });
        const invalidPostcodeControl = await this.state.form.controls.postcode.validate();
        if (!invalidPostcodeControl) {
            this.props.findAddress(this.props.editorId, new PostcodeModel(this.state.form.controls.postcode.value));
        }
    };

    handleSearchByDifferentPostcode = (): void => {
        this.state.form.controls.postcode.setValue('');
        this.setState({ currentMode: SearchModes.Search });
    };

    handleAddressSelected = (address: AddressModel | null): void => {
        if (address) {
            this.props.onChange(address);
        }
    };

    render(): JSX.Element {
        const { currentMode } = this.state;

        return (
            <div className='address-search' ref={this.addressSearchElement}>
                {currentMode === SearchModes.NotInitialized && this.renderSearchLoader()}
                {currentMode === SearchModes.Search && this.renderSearchMode()}
                {currentMode === SearchModes.Select && this.renderSelectMode()}
            </div>
        );
    }

    renderSearchLoader(): JSX.Element {
        return (
            <div className='address-search__search-loader-wrapper'>
                <div className='address-search__search-loader'>
                    <Loader show={true} overlay={true} />
                </div>
            </div>
        );
    }

    renderSearchMode(): JSX.Element {
        const { shouldShowSearchNotStartedErrorMessage } = this.state;
        const shouldShowPostcodeInstruction = this.state.form.controls.postcode.isBlurHandled
            && this.state.form.controls.postcode.errorMessage === null
            && this.state.form.controls.postcode.value.length > 0;

        return (
            <div className='address-search__enter-postcode'>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {Boolean(this.props.addressSearchEditorData?.isFindAddressProcessing) && this.renderSearchLoader()}
                        <div className='address-search__search-postcode'>
                            <Grid container>
                                <Grid item xs={4} sm={4}>
                                    <SofaInput
                                        label='Postcode *'
                                        inputProps={{ autoCorrect: 'off', autoComplete: 'postal-code' }}
                                        {...getPropsForSofaInput(this.state.form.controls.postcode)}
                                        helperText={
                                            this.state.form.controls.postcode.errorMessage
                                            || (shouldShowPostcodeInstruction && SEARCH_HAS_NOT_STARTED_WARNING_MESSAGE)
                                        }
                                        helperTextType={shouldShowPostcodeInstruction && !shouldShowSearchNotStartedErrorMessage ? 'info' : 'error'}
                                    />
                                </Grid>
                                <Grid item xs={8} sm={4}>
                                    <SofaButton
                                        text='Find address'
                                        variant='secondary'
                                        onClick={this.handleFindAddressClick}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
            </div>
        );
    }

    renderSelectMode(): JSX.Element {
        const postcodeCanBeChanged = !this.props.fixedPostcode;

        return (
            <div className='address-search__selector'>
                <AddressSelector
                    addresses={this.props.addressSearchEditorData?.foundAddresses ?? []}
                    onAddressChanged={this.handleAddressSelected}
                    ref={this.addressSelector}
                    startControlIndex={this.props.startControlIndex + COUNT_OF_CONTROLS_IN_SEARCH_MODE}
                    gtmFormName={GTM_FORM_NAME}
                />
                {postcodeCanBeChanged && <SofaLink onClick={this.handleSearchByDifferentPostcode} text='Search by a different postcode' />}
            </div>
        );
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        if (this.state.currentMode == SearchModes.NotInitialized || this.state.currentMode === SearchModes.Search) {
            return this.state.form.validate()
                .then(invalidSearchControls => {
                    if (invalidSearchControls.length === 0) {
                        // form controls are valid, but component still is in the search mode - scroll to postcode input
                        this.setState({ shouldShowSearchNotStartedErrorMessage: true });

                        return [this.state.form.controls.postcode];
                    }

                    return invalidSearchControls;
                });
        }

        return this.addressSelector.current!.validate();

    };

    public isPostcodeSearchSuccessfullyCompleted = (): boolean => {
        return Boolean(this.props.addressSearchEditorData?.foundAddresses?.length);
    };

    handleAddressSearchResponse = (): void => {
        const isAddressSearchFailed = !this.props.addressSearchEditorData!.foundAddresses || this.props.addressSearchEditorData!.addressFoundButCannotBeParsed;

        this.setState({
            isPostcodeSearchFailed: isAddressSearchFailed,
            currentMode: isAddressSearchFailed ? SearchModes.Search : SearchModes.Select
        }, () => {
            if (isAddressSearchFailed) {
                this.state.form.controls.postcode.validate();
            }
        });
    };

    postcodeServerValidator = (errorMessage: string): SofaFormValidator<string> => {
        return () => {
            return Promise.resolve(
                this.state.isPostcodeSearchFailed
                    ? errorMessage
                    : null
            );
        };
    };
}

export default AddressSearch;
export type AddressSearchType = AddressSearch;
export const ADDRESS_SEARCH_COUNT_CONTROLS = COUNT_OF_CONTROLS_IN_SEARCH_MODE + COUNT_OF_CONTROLS_IN_SELECT_MODE;
